import posthog from 'posthog-js';

interface LoggerContext {
  [key: string]: unknown;
}

interface ApiError {
  code: string;
  message: string;
}

type LogLevel = 'error' | 'warn' | 'info' | 'debug';

// Global context merged into every error event (e.g., user/org info)
let baseContext: LoggerContext = {};

export const setLoggerContext = (context: LoggerContext = {}): void => {
  baseContext = { ...baseContext, ...context };
};

const logger = (error: Error | ApiError, additionalContext: LoggerContext = {}): void => {
  // Log to console
  console.error(error);

  const mergedContext = { ...baseContext, ...additionalContext };

  // Only send to PostHog if in production
  if (import.meta.env.VITE_APP_NODE_ENV === 'production') {
    // Prefer captureException if available (custom setup), otherwise fallback to capture
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyPosthog = posthog as any;
    if (typeof anyPosthog?.captureException === 'function') {
      anyPosthog.captureException(error, mergedContext);
    } else if (error instanceof Error) {
      posthog.capture('exception', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        ...mergedContext,
      });
    } else {
      // ApiError or other plain custom error object
      posthog.capture('exception', { ...(error as object), ...mergedContext });
    }
  }
};

// Info logger for tracing and debugging
const logInfo = (message: string, context: LoggerContext = {}): void => {
  // Log to console
  console.info(`[INFO] ${message}`, context);
};

// Debug logger for tracing and debugging
const debugLogger = (message: string, context: LoggerContext = {}): void => {
  // Log to console
  console.debug(`[DEBUG] ${message}`, context);
};

// Generic logger with level support
const log = (level: LogLevel, message: string, context: LoggerContext = {}): void => {
  switch (level) {
    case 'error':
      logger(new Error(message), context);
      break;
    case 'info':
      logInfo(message, context);
      break;
    case 'debug':
      debugLogger(message, context);
      break;
    default:
      console.log(`[${level.toUpperCase()}] ${message}`, context);
  }
};

export { logger, logInfo, log };